/** @jsxImportSource @emotion/react */
import React, { Component, useState, useCallback, useEffect, useMemo } from 'react';
import { css, CSSObject, jsx } from '@emotion/react';
import * as yup from 'yup';
import Spinner from '@atlaskit/spinner';
import {
  Form,
  arrayMutators,
  validate, createDecorator,
} from 'components/form';
import { DateTime } from 'luxon';
import { useParams } from 'react-router';
import useCompanyNavigation from '../auth/useCompanyNavigation';
import { queryCache, useMutation, useQuery } from 'react-query';
import { FormattedMessage, useIntl } from "components/i18n";
import SaleEditSplitPane from './components/SaleEditSplitPane';
import SaleForm from './SaleForm';
import { Sale, VatCode } from './types';
import { PageWrapper } from '../../components/page';
import api from '../../libs/api';
import { useNotifications } from '../../components/notification';
import { deleteSale, getSaleById } from "./SalePage";
import { saveRecurringSale, saveSale } from './apiActions';
import useSaleHelpers from './hooks/useSaleHelpers';
import SaleEditHeader from './SaleEditHeader';
import SaleEditFooter from './components/SaleEditFooter';
import { useCurrentCompany } from '../auth/CurrentCompany';
import { Decorator } from 'final-form';
import { useConfirm } from "../../components/modal-dialog";

const defaultProps = {
  isNew: false,
  isRecurring: false
};

type Props = {
} & typeof defaultProps;

export const saleLinesSchema =  yup.array().of(yup.object({
  vatCodeId: yup.mixed().required('Required'),
  price: yup.mixed(),
  quantity: yup.number().required('Required')
})).min(1);

const schema = yup.object({
  invoiceNumber: yup.string().required('Required'),
  invoiceDate: yup.string().required('Required'),
  description: yup.string(),
  dueDate: yup.mixed().required('Required'),
  Lines: saleLinesSchema,
  note: yup.string(),
  footer: yup.string(),
});

export const deleteRecurringSale = async (id: string,) => api.delete(`/recurring-sales/${id}`);



const invoiceDateDecorator = createDecorator({
  field: /invoiceDate|customer/, // when invoiceDate or customer changes...
  updates: (fieldValue: any, name, allValues: Sale | undefined, prevValues: Sale | undefined) => {
    console.log('invoiceDate change', allValues);
    if((!allValues?.isNew) &&!prevValues?.invoiceNumber) {
        console.log('skip initial', allValues);
      return {}
    } // fix to skip on initialValues
    console.log('duedate',DateTime.fromISO(allValues?.invoiceDate!).plus({ days: allValues?.customer?.paymentTerm || 14 }).toISODate(),)
    return {
      dueDate: DateTime.fromISO(allValues?.invoiceDate!).plus({ days: allValues?.customer?.paymentTerm || 14 }).toISODate(),
      journalDate: allValues?.invoiceDate,
    }
  }
});
const SaleEditPage = ({isNew, isRecurring}: Props) => {

  const navigate = useCompanyNavigation();
  const currentCompany = useCurrentCompany();
  const intl = useIntl();

  const [sale, setSale] = useState<Sale|null>(null);
  const { id } = useParams();
  const { data: saleById } = useQuery(['sale', id], getSaleById, { enabled: !isNew && id });
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const saleHelpers = useSaleHelpers();

  if(!currentCompany?.invoiceSettings) {
    navigate('/sale/invoice-settings')
  }

  const getNewSale = useCallback(async(defaultVatCode: VatCode) => {
    const invoiceSettings = currentCompany?.invoiceSettings || {};
    const invoiceNumber = (await api.get('/Sales/invoice-numbers/next') as string);
      const sale = {
        isNew: true,
        status: 'draft',
        invoiceDate: DateTime.local().toISODate(),
        invoiceNumber,
        Lines: [{
          description: '',
          vatCode: defaultVatCode,
          vatCodeId: defaultVatCode?.id,
          item: null,
          itemId: null,
          price: null,
          quantity: 1,
          amount: null,
        }],
        description: invoiceSettings.defaultDescription,
        note: invoiceSettings.defaultNote,
        footer: invoiceSettings.defaultFooter,
      } as Sale;
      return sale;
  },[currentCompany?.invoiceSettings]);

  useEffect( () => {
    (async () => {
      if(!saleHelpers.vatCodes) return;
      if(!saleHelpers.getDefaultVatCode) return;
      if(sale) return;
      if(saleById) {
        setSale(saleById);
      }
      if(isNew) {
        const defaultVatCode = saleHelpers.getDefaultVatCode({});
        const newSale = await getNewSale(defaultVatCode);
        setSale(newSale);
      }
    })()
  },[saleById, isNew, saleHelpers]);


  const [save, { status, isLoading: isSaving }] = useMutation(saveSale, {
    onSuccess: (savedSale: Sale) => {
      queryCache.invalidateQueries('sales');
      queryCache.invalidateQueries(['sale', savedSale.id]);
      showSuccessNotification(
        <FormattedMessage
            id="sale.SaleEditPage.notifications.save.success.title`"
            defaultMessage="Invoice saved"
          />);
    },
    onError: (e: any) => {
      showErrorNotification(
        e.message
      )}
  });

  const [saveRecurring, { isLoading: isRecurringSaving }] = useMutation(saveRecurringSale, {
    onSuccess: (savedSale: Sale) => {
      queryCache.invalidateQueries('recurringSales');
      queryCache.invalidateQueries(['recurringSale', savedSale.id]);
      showSuccessNotification(
        <FormattedMessage
          id="sale.SaleEditPage.notifications.saveRecurring.success.title`"
          defaultMessage="Recurring invoice saved"
        />);
        navigate(`/sale/recurring-invoices`);
    },
    onError: (e: any) => {
      showErrorNotification(
        e.message
      )}
  });
  const {decorators} = saleHelpers;

  const allDecorators: Decorator[] = useMemo(()=>{
    if(decorators) {
      return [invoiceDateDecorator, ...(decorators as [])]
    }
    return []

  },[decorators, invoiceDateDecorator])

  const isLoading = !sale || !decorators;

  if (!sale || isLoading) {
    return <SaleEditSplitPane isLoading

    > <Spinner/>
      </SaleEditSplitPane>
  }



  const vatIdentificationIdRequiredMessage = intl.formatMessage({
    id: 'sale.SaleEditPage.notifications.vatIdentificationIdRequired',
    defaultMessage: 'The VAT ID number of the customer is required.',
    description: 'If customer vat registration id is missing but is required',
  });

  return (
    <Form<any>
      initialValues={sale || {}}
      onSubmit={async (values) => {
        console.log('onsubmit',values);
        if(values.Lines.some((line: { vatCodeId: string; })=>line.vatCodeId==='V-NL-V') && !values.customer.vatIdentificationNumber){
          return showErrorNotification(
            vatIdentificationIdRequiredMessage
          )
        }

        if(isRecurring){
          await saveRecurring(values);
        }else {
          try {
            const savedSale = await save(values);
            if(values.action ==='saveSend') {
              navigate(`/sale/invoices/${savedSale.id}/email`);
            }else {
              navigate(`/sale/invoices/${savedSale.id}`);
            }
          } catch (e) {
            console.log('Error saving', e);
          }
        }
      }}
      decorators={allDecorators}
      validate={validate(schema)}
      // @ts-ignore
      mutators={arrayMutators}
      subscription={{ submitting: true, pristine: true }}
    >
      {(formProps) => {
        return (
          <SaleEditSplitPane
            isLoading={false}
          >
              {!sale || isLoading?  <Spinner/>  :
                <PageWrapper fullWidth>
                  <SaleEditHeader isRecurring={isRecurring} isNew={isNew} sale={sale}/>
                  <SaleForm isNew={isNew} isRecurring={isRecurring} {...formProps} isSaving={isSaving} getDefaultVatCode={saleHelpers.getDefaultVatCode} />
                  <SaleEditFooter isNew={isNew}  isSaving={isSaving} {...formProps}/>
                </PageWrapper>}
          </SaleEditSplitPane>
        );
      }}
    </Form>
  );
};

SaleEditPage.defaultProps = defaultProps;

export default SaleEditPage;
